import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useWatch } from '@fs/zion-form'
import { useChipCollection } from '@fs/zion-ui'

export const useChipFormSync = ({ name, methods }) => {
  const selectedFieldName = `${name}Selected`
  const isUpdatingFromCollectionRef = useRef(false)

  const rawValues = useWatch({ control: methods.control, name })
  const rawSelectedValues = useWatch({
    control: methods.control,
    name: selectedFieldName,
  })

  // Memoize values to prevent unnecessary re-renders
  const values = useMemo(() => rawValues || [], [rawValues])
  const selectedValues = useMemo(() => rawSelectedValues || {}, [rawSelectedValues])

  // Create chips from form values with their selection states
  const chips = useMemo(
    () =>
      values.map((item) => ({
        label: item,
        value: item,
        'aria-label': `${item} ${name}`,
        dismissAriaLabel: `Remove ${item} ${name}`,
        selected: selectedValues[item] || false,
      })),
    [values, name, selectedValues]
  )

  // Use the built-in useChipCollection hook
  const chipCollection = useChipCollection(chips)

  // Update chip collection when form values change (including reset)
  // Only reset when the chip values themselves change, not when selection changes
  useEffect(() => {
    // Only reset if the actual chip values have changed, not just selection
    const currentValues = chipCollection.chips.map((c) => c.value).sort()
    const newValues = values.slice().sort()
    const valuesChanged = JSON.stringify(currentValues) !== JSON.stringify(newValues)

    if (valuesChanged) {
      chipCollection.resetChips(chips)
    }
  }, [values, chipCollection, chips])

  // Sync chip collection selection changes back to form
  useEffect(() => {
    // Build new selection state based on chip collection
    const newSelection = chipCollection.chips.reduce((acc, chip) => {
      acc[chip.value] = chip.selected || false
      return acc
    }, {})

    // Only update if the selection has actually changed
    const currentSelection = methods.getValues(selectedFieldName) || {}
    const selectionChanged = JSON.stringify(newSelection) !== JSON.stringify(currentSelection)

    if (selectionChanged) {
      isUpdatingFromCollectionRef.current = true
      methods.setValue(selectedFieldName, newSelection, {
        shouldDirty: true,
        shouldTouch: true,
      })

      setTimeout(() => {
        isUpdatingFromCollectionRef.current = false
      }, 0)
    }
  }, [chipCollection.selectedChips, chipCollection.chips, methods, selectedFieldName])

  // Sync form selection changes to chip collection (for external form updates)
  useEffect(() => {
    // Skip if we're currently updating from collection to avoid circular updates
    if (isUpdatingFromCollectionRef.current) {
      return
    }

    // Update chip collection selection to match form state
    chipCollection.chips.forEach((chip) => {
      const shouldBeSelected = selectedValues[chip.value] || false
      if (chip.selected !== shouldBeSelected) {
        // selectChip toggles the selection state
        chipCollection.selectChip(chip)
      }
    })
  }, [selectedValues, chipCollection])

  const addChip = useCallback(
    (value) => {
      if (value.trim()) {
        // Create the new chip
        const newChip = {
          label: value.trim(),
          value: value.trim(),
          'aria-label': `${value.trim()} ${name}`,
          dismissAriaLabel: `Remove ${value.trim()} ${name}`,
          selected: false,
        }

        // Add chip to collection
        chipCollection.addChip(newChip)

        // Update the form values
        const currentValues = methods.getValues(name) || []
        if (!currentValues.includes(value.trim())) {
          const newValues = [...currentValues, value.trim()]
          methods.setValue(name, newValues, {
            shouldDirty: true, // Mark as dirty to prevent form sync from overriding
            shouldTouch: true, // Mark as touched as well
          })
        }

        // Update the selection state
        const currentSelected = methods.getValues(selectedFieldName) || {}
        const newSelected = {
          ...currentSelected,
          [value.trim()]: false,
        }
        methods.setValue(selectedFieldName, newSelected, {
          shouldDirty: true, // Mark as dirty to prevent form sync from overriding
          shouldTouch: true, // Mark as touched as well
        })
      }
    },
    [chipCollection, methods, name, selectedFieldName]
  )

  const removeChip = useCallback(
    (chip) => {
      // Remove chip from collection
      chipCollection.removeChip(chip)

      // Update form values when a chip is dismissed
      const currentValues = methods.getValues(name) || []
      const newValues = currentValues.filter((item) => item !== chip.value)
      methods.setValue(name, newValues, {
        shouldDirty: true, // Mark as dirty to prevent form sync from overriding
        shouldTouch: true, // Mark as touched as well
      })

      // Update the selection state by removing the dismissed chip
      const currentSelected = methods.getValues(selectedFieldName) || {}
      const newSelected = { ...currentSelected }
      delete newSelected[chip.value]
      methods.setValue(selectedFieldName, newSelected, {
        shouldDirty: true, // Mark as dirty to prevent form sync from overriding
        shouldTouch: true, // Mark as touched as well
      })
    },
    [chipCollection, methods, name, selectedFieldName]
  )

  return {
    chipCollection,
    addChip,
    removeChip,
  }
}
